import StyledOutlinedTextFieldComponent from './StyledOutlinedTextFieldComponent'
import InputMask from 'react-input-mask'
import React, { useCallback, useLayoutEffect, useRef } from 'react'
import { fieldToTextField, TextFieldProps } from 'formik-material-ui'
import {
  formatPhoneNumberAsYouType,
  formatUsPhoneForMaskInput,
  isUSPhoneInput,
  normalizePhoneInputValue,
} from '../../utils/phone'

const US_PHONE_MASK = '+1 (999) 999-9999'

const sharedTextFieldProps = {
  type: 'tel' as const,
  placeholder: '+1 234 567 8900',
  inputProps: { inputMode: 'tel' as const },
}

export default function StyledOutlinedInternationalPhoneField(props: TextFieldProps) {
  const { field, form } = props
  const { ref, ...inputMaskProps } = fieldToTextField(props)
  const value = field.value ?? ''
  const useUsMask = isUSPhoneInput(value)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const wasUsMaskRef = useRef(useUsMask)

  useLayoutEffect(() => {
    if (useUsMask && !wasUsMaskRef.current) {
      const input = inputRef.current
      if (input) {
        input.focus()
        const pos = input.value.length
        input.setSelectionRange(pos, pos)
      }
    }
    wasUsMaskRef.current = useUsMask
  }, [useUsMask])

  const setInputRef = useCallback((element: HTMLInputElement | null) => {
    inputRef.current = element
  }, [])

  const handleInternationalChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(field.name, formatPhoneNumberAsYouType(event.target.value))
    },
    [field.name, form],
  )

  const handleUsMaskChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value
      const normalized = normalizePhoneInputValue(raw)
      form.setFieldValue(field.name, normalized === '' ? '' : raw)
    },
    [field.name, form],
  )

  if (useUsMask) {
    return (
      // @ts-ignore — fieldToTextField returns MUI TextField props incompatible with InputMask types
      <InputMask
        {...inputMaskProps}
        value={formatUsPhoneForMaskInput(value)}
        mask={US_PHONE_MASK}
        maskChar=" "
        alwaysShowMask={false}
        onChange={handleUsMaskChange}
      >
        {(inputProps: any) => (
          <StyledOutlinedTextFieldComponent
            {...inputProps}
            {...sharedTextFieldProps}
            ref={ref}
            inputRef={setInputRef}
          />
        )}
      </InputMask>
    )
  }

  return (
    <StyledOutlinedTextFieldComponent
      {...inputMaskProps}
      {...sharedTextFieldProps}
      ref={ref}
      inputRef={setInputRef}
      value={value}
      onChange={handleInternationalChange}
    />
  )
}
